/**
 * @author NikolaV
 */

import { MapTileLayers } from '@openlayers/tile-layers/MapTileLayers';
import { IMapTilelayer } from '@shared/openlayers/tile-layers/TileLayerService';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Catch } from '@shared/ui-react/Catch';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { Tree } from '@shared/ui-react/Tree';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './TileLayersPanelComponent.scss';


@observer
export class TileLayersPanelComponent extends React.Component<{
    treeData: ITreeData | null,
    tileLayers: MapTileLayers
}> {
    @observable.ref treeWithFakeLayers: ITreeData = null;
    treeWrapper: Tree = null;
    @observable isLoading = false;

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        if (this.props.tileLayers) {
            this.init();
        }
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps.treeData !== this.props.treeData) {
            this.init();
        }
    }

    async init() {
        if (this.treeWithFakeLayers) {
            return; // already initialized
        }

        let treeWithFakeLayers: ITreeData = {
            id: kuura.getRootFolderId(),
            models: [],
            folders: [],
        } as any;

        // only wait for file tree if any layer is actually linked to file tree node
        if (this.props.tileLayers.layersDefinitions.some(ld => ld.fileUuid)) {
            if (this.props.treeData) {
                treeWithFakeLayers = JSON.parse(JSON.stringify(this.props.treeData))
                if (!treeWithFakeLayers.models) treeWithFakeLayers.models = [];
            } else {
                // tree not loaded, try again later
                return null;
            }
        }

        this.props.tileLayers.layersDefinitions.forEach(ld => {
            let target = treeWithFakeLayers;
            if (ld.fileUuid) {
                const node = FileTreeService.findNodeByUuid(treeWithFakeLayers, ld.fileUuid);
                if (node && node.parentUuid) {
                    target = FileTreeService.findNodeByUuid(treeWithFakeLayers, node.parentUuid);
                }
            }
            target.models.push({
                name: ld.name,
                id: ld.id,
                uuid: ld.uuid,
                projectLayer: true,
                models: [],
                description: null,
                filesize: 1,
                timestamp: 0,
                version: 1
            } as any)
        })
        this.treeWithFakeLayers = treeWithFakeLayers;

        FileTreeService.eachPostOrder(treeWithFakeLayers, 0, node => {
            if (node.id !== kuura.getRootFolderId() && node.models) {
                node.models = node.models.sort((a, b) => alphaNumSort(a.name, b.name));
            }
        })
    }

    render() {
        const checkedUuids = this.props.tileLayers.activeUuids;
        if (!this.treeWithFakeLayers) return <div className='overlay-loading no-bg' style={{ height: '100%' }}></div>;

        return (
            <div className='tiles-panel__wrapper'>
                <Catch>
                    <nav className='flex-row sidebar-toolbar ' style={{ justifyContent: 'end' }}>
                        <Dropdown closeOnBodyClick={true} buttonStyle={{ paddingLeft: 10, paddingRight: 10 }} className='ik-button-secondary--subtle'>
                            <i className='iki iki-more'/>
                            <ul className='dropdown-list clickable'>
                                <li onClick={this.props.tileLayers.deactivateAll}>{__('general.deselect_all')}</li>
                            </ul>
                        </Dropdown>
                    </nav>

                    <Tabs displayValue='flex' flexDirection='column'>
                        <TabPane title={__('js_map.layers.all')}>
                            <Tree ref={el => this.treeWrapper = el}
                                treeData={this.treeWithFakeLayers}
                                childFilter={c => {
                                    return c.tolerance || c.projectLayer;
                                }}
                                onChecked={action((node, allChanged) => {
                                    // action() is needed here because we want all layer activations to happen in batch, and only then rerender the panel
                                    // otherwise after first activateByIdOrUuid we uncheck all other layers and only first is activated
                                    allChanged.forEach(node => {
                                        if (node.isFile()) {
                                            if (node.state.checked) {
                                                this.props.tileLayers.activateByIdOrUuid(node.uuid, false);
                                            } else {
                                                this.props.tileLayers.deactivateLayer(node.uuid);
                                            }
                                        }
                                    })
                                })}
                                headerControls={true}
                                storageKey='map-tile-layers-panel'
                                checkedUuids={checkedUuids}
                                expandOnStart={'all'}
                                includesFiles={true}
                                hideEmptyFoldersAlways={true}
                                hideEmptyFolders={true}
                                selectionCheckbox={false}
                                showRoot={false}
                                showContextMenu={false}
                            />
                        </TabPane>
                        <TabPane title={__('js_map.layers.activated')}>
                            <div style={{ overflow: 'auto' }}>
                                {checkedUuids.length ?
                                    <LayersList
                                        mapTileLayers={this.props.tileLayers}
                                        onSortEnd={this.onSortEnd}
                                    /> :
                                    <div className='text-center'>{__('js_map.no_tile_layers_selected')}</div>
                                }
                            </div>
                        </TabPane>
                    </Tabs>
                </Catch>
            </div>
        );
    }

    onSortEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const activeUuids = [...this.props.tileLayers.activeUuids];
            const oldIndex = activeUuids.findIndex(uuid => uuid === active.id);
            const newIndex = activeUuids.findIndex(uuid => uuid === over.id);

            const newActiveUuids = arrayMove(activeUuids, oldIndex, newIndex);
            this.props.tileLayers.activeUuids = newActiveUuids;
            this.props.tileLayers.resortLayersZindex();
        }
    };
}


interface ILayersListProps {
    mapTileLayers: MapTileLayers;
    onSortEnd: (event: DragEndEvent) => void;
}

const LayersList = observer(({ mapTileLayers, onSortEnd }: ILayersListProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor)
    );

    const tileLayers: MapTileLayers = mapTileLayers;
    const onlyActive = tileLayers.activeUuids.map(uuid => tileLayers.layersDefinitions.find(ld => ld.uuid === uuid));

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onSortEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}>

            <SortableContext
                items={tileLayers.activeUuids}
                strategy={verticalListSortingStrategy}>

                <div>
                    {onlyActive.map((layer) => (
                        <SortableLayerItem
                            key={layer.uuid}
                            id={layer.uuid}
                            layer={layer}
                            mapTileLayers={tileLayers}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
});

interface ILayerItemProps {
    layer: IMapTilelayer;
    mapTileLayers: MapTileLayers;
    style?: React.CSSProperties;
    dragHandleProps?: any;
}

const LayerItem = observer(({ layer, mapTileLayers, style = {}, dragHandleProps = {} }: ILayerItemProps) => {
    const isChecked = mapTileLayers.activeUuids.includes(layer.uuid);

    return <div className='tiles-list__item' style={style}>
        <span className='sortable__drag-handle' {...dragHandleProps}>፧</span>
        <div className='flex-row'>
            <div className='flex-row ai-center tiles-list__item__name'>
                <span><input type='checkbox' checked={isChecked} onClick={() => mapTileLayers.toggleLayer(layer.uuid)} readOnly/></span>
                <span className='name'>
                    {layer.name}
                    {mapTileLayers.connectionProblem[layer.uuid] &&
                        <span style={{ marginLeft: 5 }} className='badge badge-warning' title={__('js_map.layers.connection_problems')}>!</span>
                    }
                </span>
            </div>
            {isChecked &&
                <div className='flex-row ai-center tiles-list__item__slider'>
                    <input type='range' min={20} max={100} step={10} value={mapTileLayers.opacities[layer.uuid]}
                        onChange={e => mapTileLayers.setOpacity(layer.uuid, parseInt(e.currentTarget.value))} />
                    <strong style={{ width: '3em' }}> {mapTileLayers.opacities[layer.uuid]}%</strong>
                </div>
            }
        </div>
    </div>;
});

const SortableLayerItem = observer(({ id, layer, mapTileLayers }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const dragHandleProps = {
        ...attributes,
        ...listeners
    };

    return (
        <div ref={setNodeRef}>
            <LayerItem
                layer={layer}
                mapTileLayers={mapTileLayers}
                dragHandleProps={dragHandleProps}
                style={style}
            />
        </div>
    );
});
